// Mid-Mile Module TypeScript Interfaces
// Extends existing TMS structure for mid-mile operations

import { Timestamp } from 'firebase/firestore'

// ============================================================================
// CORE MID-MILE DATA MODELS
// ============================================================================

export interface MidMileShipment {
  id: string
  shipmentNumber: string
  status: ShipmentStatus
  mode: ShipmentMode
  
  // Origin & Destination
  originHub: HubLocation
  destinationHub: HubLocation
  route: TransitLane
  
  // Cargo Details
  cargo: CargoDetails
  specialRequirements: SpecialRequirement[]
  
  // Timing
  pickupWindow: TimeWindow
  deliveryWindow: TimeWindow
  estimatedTransitTime: number // hours
  
  // Parties
  shipper: ShipperInfo
  carrier?: CarrierInfo
  broker?: BrokerInfo
  
  // Financial
  rateTable: string // Reference to rate table ID
  estimatedCost: number
  actualCost?: number
  billingStatus: BillingStatus
  
  // Bidding
  biddingEnabled: boolean
  auctionEndTime?: Timestamp
  bids: Bid[]
  winningBid?: Bid
  
  // Tracking
  trackingNumber?: string
  currentLocation?: GeoLocation
  milestones: Milestone[]
  
  // Metadata
  createdAt: Timestamp
  updatedAt: Timestamp
  createdBy: string
  notes?: string
  
  // Integration with existing TMS
  linkedOrders?: string[] // References to TMS_ORDERS
  distributionZone?: string // Final mile distribution zone
}

export interface HubLocation {
  id: string
  name: string
  type: HubType
  address: Address
  contact: ContactInfo
  operatingHours: OperatingHours
  capabilities: HubCapability[]
  geoLocation: GeoLocation
}

// ============================================================================
// TRANSIT LANE (extends existing LANES collection)
// ============================================================================

export interface TransitLane {
  id: string
  name: string
  origin: string // Hub ID
  destination: string // Hub ID
  distance: number // km - ADDED for mid-mile functionality
  estimatedTime: number // hours (maps to totalLeadTime)
  restrictions: LaneRestriction[]
  cost: number
  capacity: number
  segments: LaneSegment[]
  
  // Existing LANES fields
  environmentalConsiderations?: any[]
  freightType?: any[]
  technologyIntegration?: any[]
  hubs?: string[]
  platooningRegulations?: {
    allowed: boolean
    minFollowingDistance: number
  }
  timeOfDayRestrictions?: string
  totalLeadTime?: number // maps to estimatedTime
}

export interface CargoDetails {
  weight: number // kg
  volume: number // m³
  pieces: number
  pallets: number
  dimensions: Dimensions
  freightClass: string
  commodityType: string
  hazmat: boolean
  temperatureControlled: boolean
  specialHandling: string[]
}

export interface Bid {
  id: string
  carrierId: string
  carrierName: string
  amount: number
  currency: string
  transitTime: number // hours
  equipment: string[]
  specialConditions: string[]
  submittedAt: Timestamp
  status: BidStatus
  isWinning: boolean
}

export interface Milestone {
  id: string
  type: MilestoneType
  location: string
  timestamp: Timestamp
  status: MilestoneStatus
  notes?: string
  attachments?: Attachment[]
}

// ============================================================================
// UNIFIED CONTRACTOR MANAGEMENT (Consolidates Mid-Mile Carriers & Final-Mile Contractors)
// ============================================================================

export interface Contractor {
  id: string
  companyName: string
  active: boolean
  
  // Service Type Flags
  isMidMileCarrier: boolean
  isFinalMileContractor: boolean
  isCustomerContractor: boolean
  isMetro: boolean
  isShyftbase: boolean
  reverseLogistics: boolean
  
  // Contact Information
  contact: ContactInfo
  address: Address
  
  // Final-Mile Specific Fields (from CONTRACTORS collection)
  tier: string
  minPayable: string
  escrow: {
    id: string | null
    name: string | null
    currentEscrow: number
  }
  hourlyRate: number
  rateCalculation: {
    fsc: boolean
    condition: boolean
    zone: boolean
    service: boolean
    orderStatus: boolean
    hourlyRate: boolean
  }
  trucks: string
  
  // Mid-Mile Specific Fields (only if isMidMileCarrier = true)
  midMile?: {
    mcNumber: string
    dotNumber: string
    status: CarrierStatus
    verification: VerificationStatus
    
    // Capabilities
    equipment: Equipment[]
    serviceAreas: string[] // Hub IDs
    certifications: Certification[]
    
    // Performance Metrics
    rating: number // 1-5
    onTimePercentage: number
    damageRate: number
    totalShipments: number
    
    // Financial
    paymentTerms: number // days
    insurance: InsuranceInfo
    creditLimit: number
    
    // Bidding Preferences
    autoBid: boolean
    minBidAmount: number
    preferredLanes: string[] // Lane IDs
  }
  
  // Metadata
  createdAt: Timestamp
  updatedAt: Timestamp
  createdBy: string
  updatedBy: string
}

// Legacy interface for backward compatibility
// Note: MidMileCarrier is now equivalent to Contractor, so we can use Contractor directly
// export interface MidMileCarrier extends Contractor {
//   // This interface now extends Contractor for backward compatibility
//   // All mid-mile specific fields are now in the midMile property
// }

export interface Equipment {
  id: string
  type: EquipmentType
  capacity: number
  dimensions: Dimensions
  temperatureControlled: boolean
  hazmatCertified: boolean
  available: boolean
}

// ============================================================================
// RATE MANAGEMENT
// ============================================================================

export interface MidMileRateTable {
  id: string
  name: string
  client: string // Vendor ID
  rateType: RateType
  baseRate: number
  currency: string
  status: RateTableStatus
  effectiveDate: Timestamp
  expiryDate?: Timestamp
  
  // Rate Structure
  zones: RateZone[]
  conditions: RateCondition[]
  services: RateService[]
  
  // Billing Configuration
  billingMethod: BillingMethod
  fuelSurcharge: FuelSurchargeConfig
  taxes: TaxConfig
  
  // Metadata
  createdAt: Timestamp
  updatedAt: Timestamp
  createdBy: string
}

export interface RateZone {
  id: string
  name: string
  originHubs: string[]
  destinationHubs: string[]
  baseRate: number
  minRate: number
  maxRate: number
  ratePerKm?: number
  ratePerPallet?: number
  ratePerCwt?: number
}

// ============================================================================
// TARIFF MANAGEMENT
// ============================================================================

export interface MidMileTariff {
  id: string
  name: string
  route: string // e.g., "US-Canada", "US-Mexico"
  currency: string
  status: TariffStatus
  effectiveDate: Timestamp
  expiryDate?: Timestamp
  
  // Tariff Structure
  lanes: TariffLane[]
  duties: DutyConfig[]
  taxes: TaxConfig
  
  // Documentation
  documents: Document[]
  
  // Metadata
  createdAt: Timestamp
  updatedAt: Timestamp
  createdBy: string
}

// ============================================================================
// BIDDING SYSTEM
// ============================================================================

export interface MidMileAuction {
  id: string
  shipmentId: string
  status: AuctionStatus
  startTime: Timestamp
  endTime: Timestamp
  
  // Configuration
  minBids: number
  autoAward: boolean
  bidIncrement: number
  timeExtension: number // minutes
  
  // Participants
  invitedCarriers: string[] // Carrier IDs
  participatingCarriers: string[]
  
  // Results
  bids: Bid[]
  winningBid?: Bid
  awardedTo?: string // Carrier ID
  
  // Metadata
  createdAt: Timestamp
  updatedAt: Timestamp
  createdBy: string
}

// ============================================================================
// ENUMS AND CONSTANTS
// ============================================================================

export enum ShipmentStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  BIDDING = 'bidding',
  AWARDED = 'awarded',
  IN_TRANSIT = 'in_transit',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled'
}

export enum ShipmentMode {
  TRUCKLOAD = 'truckload',
  LTL = 'ltl',
  INTERMODAL = 'intermodal',
  DEDICATED = 'dedicated',
  EXPEDITED = 'expedited',
  HOTSHOT = 'hotshot',
  FLATBED = 'flatbed',
  REFRIGERATED = 'refrigerated',
  DRY_VAN = 'dry_van',
  CONTAINER = 'container',
  BULK = 'bulk',
  HEAVY_HAUL = 'heavy_haul',
  WHITE_GLOVE = 'white_glove',
  CROSS_DOCK = 'cross_dock',
  POOL_DISTRIBUTION = 'pool_distribution',
  FINAL_MILE = 'final_mile',
  LOCAL = 'local',
  REGIONAL = 'regional'
  // AIR = 'air',
  // OCEAN = 'ocean'
}

export enum HubType {
  WAREHOUSE = 'warehouse',
  DISTRIBUTION_CENTER = 'distribution_center',
  CROSS_DOCK = 'cross_dock',
  HUB = 'hub',
  TERMINAL = 'terminal'
}

export enum CarrierStatus {
  ACTIVE = 'active',
  PENDING = 'pending',
  SUSPENDED = 'suspended',
  INACTIVE = 'inactive'
}

export enum VerificationStatus {
  PENDING = 'pending',
  VERIFIED = 'verified',
  REJECTED = 'rejected'
}

export enum BidStatus {
  SUBMITTED = 'submitted',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  WITHDRAWN = 'withdrawn'
}

export enum MilestoneType {
  PICKUP = 'pickup',
  IN_TRANSIT = 'in_transit',
  DELIVERY = 'delivery',
  CUSTOMS = 'customs',
  INSPECTION = 'inspection'
}

export enum MilestoneStatus {
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  DELAYED = 'delayed'
}

export enum EquipmentType {
  DRY_VAN = 'dry_van',
  REEFER = 'reefer',
  FLATBED = 'flatbed',
  POWER_ONLY = 'power_only',
  CONTAINER = 'container'
}

export enum RateType {
  FIXED = 'fixed',
  PER_KM = 'per_km',
  PER_PALLET = 'per_pallet',
  PER_CWT = 'per_cwt',
  PER_HOUR = 'per_hour'
}

export enum RateTableStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  EXPIRED = 'expired',
  INACTIVE = 'inactive'
}

export enum BillingMethod {
  PREPAID = 'prepaid',
  COLLECT = 'collect',
  THIRD_PARTY = 'third_party'
}

export enum TariffStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  EXPIRED = 'expired',
  INACTIVE = 'inactive'
}

export enum AuctionStatus {
  SCHEDULED = 'scheduled',
  OPEN = 'open',
  CLOSED = 'closed',
  AWARDED = 'awarded',
  CANCELLED = 'cancelled'
}

export enum BillingStatus {
  PENDING = 'pending',
  CALCULATED = 'calculated',
  INVOICED = 'invoiced',
  PAID = 'paid'
}

// ============================================================================
// SUPPORTING INTERFACES
// ============================================================================

export interface Address {
  street: string
  city: string
  state: string
  postalCode: string
  country: string
  unit?: string
}

export interface ContactInfo {
  name: string
  email: string
  phone: string
  fax?: string
}

export interface GeoLocation {
  latitude: number
  longitude: number
  accuracy?: number
}

export interface TimeWindow {
  start: Timestamp
  end: Timestamp
}

export interface Dimensions {
  length: number
  width: number
  height: number
  unit: 'cm' | 'inch'
}

export interface SpecialRequirement {
  type: string
  description: string
  required: boolean
}

export interface ShipperInfo {
  id: string
  name: string
  contact: ContactInfo
  address: Address
}

export interface CarrierInfo {
  id: string
  name: string
  contact: ContactInfo
  equipment: string[]
}

export interface BrokerInfo {
  id: string
  name: string
  contact: ContactInfo
  commission: number
}

export interface LaneRestriction {
  type: string
  description: string
  appliesTo: string[]
}

export interface LaneSegment {
  id: string
  origin: string
  destination: string
  distance: number
  estimatedTime: number
  mode: string
}

export interface HubCapability {
  type: string
  description: string
  available: boolean
}

export interface OperatingHours {
  monday: TimeWindow
  tuesday: TimeWindow
  wednesday: TimeWindow
  thursday: TimeWindow
  friday: TimeWindow
  saturday: TimeWindow
  sunday: TimeWindow
}

export interface Certification {
  type: string
  number: string
  expiryDate: Timestamp
  issuingAuthority: string
}

export interface InsuranceInfo {
  provider: string
  policyNumber: string
  coverage: number
  expiryDate: Timestamp
}

export interface RateCondition {
  id: string
  name: string
  type: string
  value: number
  application: string
}

export interface RateService {
  id: string
  name: string
  rate: number
  type: string
  required: boolean
}

export interface FuelSurchargeConfig {
  enabled: boolean
  basePrice: number
  currentPrice: number
  percentage: number
}

export interface TaxConfig {
  enabled: boolean
  rate: number
  type: string
  jurisdiction: string
}

export interface TariffLane {
  id: string
  origin: string
  destination: string
  baseRate: number
  duties: number
  taxes: number
}

export interface DutyConfig {
  commodityType: string
  rate: number
  description: string
}

export interface Document {
  id: string
  name: string
  type: string
  url: string
  uploadedAt: Timestamp
}

export interface Attachment {
  id: string
  name: string
  type: string
  url: string
  uploadedAt: Timestamp
}

// ============================================================================
// FIREBASE COLLECTION NAMES
// ============================================================================

export const MIDMILE_COLLECTIONS = {
    SHIPMENTS: 'MIDMILE_SHIPMENTS',
    CONTRACTORS: 'CONTRACTORS', // Updated to use unified CONTRACTORS collection
    RATE_TABLES: 'MIDMILE_RATE_TABLES',
    TARIFFS: 'MIDMILE_TARIFFS',
    AUCTIONS: 'MIDMILE_AUCTIONS',
    BIDS: 'MIDMILE_BIDS',
    HUBS: 'DISTRIBUTIONZONES', // Use existing DISTRIBUTIONZONES collection
    LANES: 'LANES' // Use existing LANES collection
} as const

// ============================================================================
// FORM DATA INTERFACES
// ============================================================================

export interface MidMileShipmentFormData {
  // Basic Information
  shipmentNumber: string
  mode: ShipmentMode
  status: ShipmentStatus
  
  // Origin & Destination
  originHub: string
  destinationHub: string
  route: string
  
  // Cargo Details
  weight: number
  volume: number
  pieces: number
  pallets: number
  freightClass: string
  commodityType: string
  hazmat: boolean
  temperatureControlled: boolean
  specialHandling: string[]
  
  // Timing
  pickupWindow: {
    start: Date
    end: Date
  }
  deliveryWindow: {
    start: Date
    end: Date
  }
  
  // Parties
  shipper: string // Vendor ID
  shipperContact: ContactInfo
  shipperAddress: Address
  carrier?: string
  broker?: string
  
  // Bidding
  biddingEnabled: boolean
  
  // Additional
  notes: string
  specialRequirements: SpecialRequirement[]
  
  // Scheduling
  schedule?: {
    enabled: boolean
    frequency: 'once' | 'daily' | 'weekly' | 'monthly' | 'custom'
    startDate: Date
    startTime: string
    endDate?: Date
    daysOfWeek?: number[]
    dayOfMonth?: number
    interval?: number
    timezone: string
  }
} 